// SCR-INVENTORY, MOD-ITEM, MOD-UPGRADE, MOD-DISMANTLE ⛔ — E05-T06, ux/05 §3–6.
// Цена и прирост улучшения — Item.nextUpgrade из договора (считает сервер); выход разбора и продажи до
// подтверждения — clientConfig.values.dismantle (решение Р-37). Надетые предметы в массовую выборку не попадают.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Item, Rarity, Slot } from '../content/types.js';
import { useApp } from '../app/app.js';
import { RARITY_NAMES, SLOT_NAMES, STAT_NAMES, itemDesc, itemLevel, itemName } from '../content/catalog.js';
import { amount, num } from '../i18n/format.js';
import { ApiCallError } from '../net/api-client.js';
import { clientConfig, inventory, online, player, saveCache, setInventoryItems, setWallet, tutorialStep, wallet } from '../state/game.js';
import { storage } from '../state/storage.js';
import { stage, type ScreenProps, type SheetProps } from '../ui/App.js';
import { Dialog, ErrorBanner, ItemTile, ScreenTitle, Sheet, Skeleton, slotGlyph, useActionBar, useArmDelay } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useInventory } from './char.js';
import { completeStep, setTut, startTutorial, TEXT } from '../tutorial/flow.js';
import { I } from '../assets/icons.js';
import { t as tr } from '../i18n/t.js';
import { SoldBlock, TransferOffer, bestLoadout, previewsForBest } from './transfer.js';
import { useLoad } from '../ui/use-load.js';

type StatKey = keyof Item['stats'];
const STAT_ORDER: StatKey[] = ['attack', 'hp', 'def', 'crit', 'critMult', 'dodge'];
const SLOTS: Slot[] = ['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory'];
const RARITIES: Rarity[] = ['common', 'good', 'rare', 'epic', 'legendary'];
const RARITY_RANK: Record<Rarity, number> = { common: 0, good: 1, rare: 2, epic: 3, legendary: 4 };

export function fmtStat(k: StatKey, v: number): string {
  return k === 'crit' || k === 'dodge' ? `${(v * 100).toFixed(1)}%` : k === 'critMult' ? `×${v.toFixed(2)}` : num(v);
}

/** Разница с надетым в том же слоте по каждой характеристике (ux/05 §4). */
export function compareStats(item: Item, worn: Item | undefined): { k: StatKey; v: number; diff: number }[] {
  const keys = STAT_ORDER.filter((k) => item.stats[k] !== undefined || worn?.stats[k] !== undefined);
  return keys.map((k) => ({ k, v: item.stats[k] ?? 0, diff: (item.stats[k] ?? 0) - (worn?.stats[k] ?? 0) }));
}

interface DismantleCfg { scrap?: Partial<Record<Rarity, number>>; sell?: Partial<Record<Rarity, number>> }

/** Предпросмотр MOD-UPGRADE (ux/05 §5): null у nextUpgrade — потолок; нет поля — цену назовёт сервер. */
export function upgradePreview(item: Item) {
  const n = item.nextUpgrade;
  return {
    cost: n ? { nuts: n.cost.nuts ?? 0, scrap: n.cost.scrap ?? 0 } : null,
    next: n ? n.stats : null,
    atMax: n === null,
  };
}

/** Итог разбора или продажи до подтверждения (ux/05 §6): лом/гайки по редкости и уровню улучшения. */
export function dismantleYield(items: Item[], cfg: Record<string, unknown>, mode: 'dismantle' | 'sell'): number | null {
  const d = cfg['dismantle'] as DismantleCfg | undefined;
  const table = mode === 'dismantle' ? d?.scrap : d?.sell;
  if (!table) return null;
  return items.reduce((s, i) => s + (table[i.rarity] ?? 0) * (mode === 'dismantle' ? 1 + i.upgradeLevel : 1), 0);
}

export function InventoryScreen(p: ScreenProps) {
  const app = useApp();
  const inv = useInventory();
  const tutorial = !!p.params['tutorial'] || tutorialStep.value === 'T06';
  const [slot, setSlot] = useState<Slot | 'all' | 'sold'>((p.params['slot'] as Slot) ?? 'all');
  const sales = useLoad(() => app.call('getSales'));
  const [rarity, setRarity] = useState<Rarity | 'all'>('all');
  const [select, setSelect] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const press = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { void stage.value?.show('none'); }, [stage.value]);
  useEffect(() => { storage.set('invSlot', slot); }, [slot]);
  const items = slot === 'sold' ? [] : (inv.data?.items ?? [])
    .filter((i) => (slot === 'all' || i.slot === slot) && (rarity === 'all' || i.rarity === rarity))
    .sort((a, b) => Number(b.equipped) - Number(a.equipped) || RARITY_RANK[b.rarity] - RARITY_RANK[a.rarity] || b.upgradeLevel - a.upgradeLevel);
  const chosen = (inv.data?.items ?? []).filter((i) => picked.has(i.itemId));
  const toggle = (i: Item) => {
    const n = new Set(picked);
    if (n.has(i.itemId)) n.delete(i.itemId); else n.add(i.itemId);
    setPicked(n);
  };
  // Берём свежий список из общего состояния: замыкание кнопки может пережить отрисовку с пустой сумкой.
  const quick = (pred: (i: Item) => boolean) => setPicked(new Set((inventory.value?.items ?? inv.data?.items ?? []).filter((i) => !i.equipped && !i.locked && pred(i)).map((i) => i.itemId)));
  const worn = new Map((inv.data?.items ?? []).filter((i) => i.equipped).map((i) => [i.slot, i]));
  const scrap = dismantleYield(chosen, clientConfig.value, 'dismantle');
  // «Надеть лучшее» (E05-T16): сравнение с надетой улучшенной — по силе новой ПОСЛЕ переноса (превью сервера).
  const wearBest = async () => {
    const all = inventory.value?.items ?? [];
    const best = bestLoadout(all, await previewsForBest(app.call, all));
    if (!best.length) { showToast(tr('CL-BEST-NONE')); return; }
    try {
      for (const it of best) {
        const r = await app.call('equip', { body: { itemId: it.itemId } });
        setInventoryItems((xs) => xs.map((x) => ({ ...x, equipped: r.equipped.some((e) => e.itemId === x.itemId) })));
        if (player.value) player.value = { ...player.value, derived: r.stats };
      }
      saveCache();
      app.haptic('medium');
      showToast(tr('CL-BEST-DONE', { n: best.length }), 'success');
    } catch (e) { app.handleError(e, { resource: tr('CL-SCREENS-INVENTORY-16') }); }
  };
  useActionBar(select ? {
    back: true,
    secondary: { text: tr('CL-SCREENS-INVENTORY-01'), tone: 'outline', onClick: () => { setSelect(false); setPicked(new Set()); } },
    main: chosen.length
      ? { text: tr('CL-SCREENS-INVENTORY-02', { p0: chosen.length, p1: scrap !== null ? tr('CL-SCREENS-INVENTORY-SCRAP-TO', { n: scrap }) : '' }), testId: 'mass-dismantle', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-DISMANTLE', { itemIds: chosen.map((i) => i.itemId), mode: 'dismantle' }) }
      : { text: tr('CL-SCREENS-INVENTORY-03'), disabled: true, onClick: () => {} },
  } : {
    back: true,
    ...(tutorial ? {} : {
      secondary: { text: tr('CL-BEST-WEAR'), tone: 'outline' as const, testId: 'wear-best', disabled: !online.value || !inv.data?.items.length, onClick: () => wearBest() },
      main: { text: tr('CL-SCREENS-INVENTORY-04'), tone: 'outline' as const, testId: 'select-mode', disabled: !online.value || !inv.data?.items.length, onClick: () => setSelect(true) },
    }),
  });
  const filters = (
    <div class="col" style={{ gap: 6 }}>
      <div class="chips" role="tablist" aria-label={tr('CL-SCREENS-INVENTORY-05')}>
        <button class={`chip ${slot === 'all' ? 'on' : ''}`} onClick={() => setSlot('all')}>{tr('CL-SCREENS-INVENTORY-06')}</button>
        {SLOTS.map((s) => <button key={s} class={`chip ${slot === s ? 'on' : ''}`} data-testid={`filter-${s}`} aria-label={SLOT_NAMES[s]} onClick={() => setSlot(s)}>{slotGlyph(s, 20)}</button>)}
        <button class={`chip ${slot === 'sold' ? 'on' : ''}`} data-testid="filter-sold" onClick={() => setSlot('sold')}>{tr('CL-SOLD-TAB')}</button>
      </div>
      <div class="chips" aria-label={tr('CL-SCREENS-INVENTORY-07')}>
        <button class={`chip ${rarity === 'all' ? 'on' : ''}`} onClick={() => setRarity('all')}>{tr('CL-SCREENS-INVENTORY-08')}</button>
        {RARITIES.map((r) => <button key={r} class={`chip rarity-${r} ${rarity === r ? 'on' : ''}`} data-testid={`rarity-${r}`} onClick={() => setRarity(r)}><span style={{ color: 'var(--r)' }}>●</span> {RARITY_NAMES[r]}</button>)}
      </div>
    </div>
  );
  return (
    <>
      <ScreenTitle right={<span class="small muted">{inv.data ? `${inv.data.items.length} / 40` : ''}</span>}>{tr('CL-SCREENS-INVENTORY-09')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-INVENTORY">
        <div class="page col">
          {inv.error && !inv.offline ? <ErrorBanner what={tr('CL-SCREENS-INVENTORY-09')} onRetry={inv.reload} /> : null}
          {inv.offline ? <div class="banner small">{tr('CL-SCREENS-INVENTORY-10')}</div> : null}
          {!tutorial ? filters : null}
          {select ? (
            <div class="chips">
              <button class="chip" onClick={() => quick((i) => i.rarity === 'common')}>{tr('UI-BAG-PICK-COMMON')}</button>
              <button class="chip" data-testid="quick-worse" onClick={() => quick((i) => { const w = worn.get(i.slot); return !!w && compareStats(i, w).every((d) => d.diff <= 0); })}>{tr('CL-SCREENS-INVENTORY-11')}</button>
            </div>
          ) : null}
          {!inv.data && inv.loading ? <div class="item-grid">{Array.from({ length: 12 }, (_, i) => <div key={i} class="skeleton" style={{ aspectRatio: '1' }} />)}</div> : null}
          {slot === 'sold' ? (sales.data ? <SoldBlock sales={sales.data.items} onChange={sales.reload} /> : <div class="skeleton" style={{ height: 64 }} />) : null}
          {inv.data && !items.length && slot !== 'sold' ? (
            <div class="empty" data-testid="inv-empty"><I n="bag" size={48} class="ill" />{tr('UI-BAG-EMPTY')}<button class="btn small accent" onClick={() => app.nav.selectTab('map')}>{tr('CL-SCREENS-INVENTORY-12')}</button></div>
          ) : null}
          <div class="item-grid">
            {items.map((i) => (
              <ItemTile
                key={i.itemId}
                item={i}
                selected={picked.has(i.itemId)}
                data-tut={tutorial && i.defId === 'EQ-HEAD-01' ? 'tut-cap' : undefined}
                onPointerDown={() => { if (!tutorial) press.current = setTimeout(() => { setSelect(true); if (!i.equipped) toggle(i); }, 500); }}
                onPointerUp={() => { if (press.current) clearTimeout(press.current); }}
                onClick={() => {
                  if (select) { if (i.equipped) showToast(tr('CL-SCREENS-INVENTORY-13')); else toggle(i); return; }
                  app.nav.openSheet('MOD-ITEM', { itemId: i.itemId, tutorial });
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export function ItemSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['itemId']);
  const tutorial = !!entry.params['tutorial'];
  const all = inventory.value?.items ?? [];
  const item = all.find((i) => i.itemId === id);
  const me = player.value;
  useEffect(() => { if (tutorial) setTut({ target: 'item-equip', say: null, hint: null }); }, []);
  useEffect(() => { if (!item) app.call('getInventory').then((d) => { inventory.value = d; saveCache(); }).catch(() => {}); }, []);
  if (!item) return <Sheet entry={entry} title={tr('CL-SCREENS-INVENTORY-14')}><Skeleton h={120} /></Sheet>;
  const worn = all.find((i) => i.equipped && i.slot === item.slot && i.itemId !== item.itemId);
  const need = itemLevel(item.defId);
  const low = !!me && need > me.level;
  const equip = async () => {
    try {
      const r = await app.call('equip', { body: { itemId: item.itemId } });
      setInventoryItems((xs) => xs.map((x) => ({ ...x, equipped: r.equipped.some((e) => e.itemId === x.itemId) })));
      if (me) player.value = { ...me, derived: r.stats };
      app.haptic('medium');
      if (tutorial || tutorialStep.value === 'T06') {
        setTut({ target: null, say: TEXT.T06say, hint: null });
        const res = await completeStep(app, 'T06', 'equip');
        await new Promise((x) => setTimeout(x, 1200));
        app.nav.closeSheets();
        app.nav.pop();
        startTutorial(app, res.step);
      } else app.nav.popSheet();
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'LEVEL_TOO_LOW') { showToast(tr('CL-SCREENS-INVENTORY-15', { p0: need }), 'error'); return; }
      app.handleError(e, { resource: tr('CL-SCREENS-INVENTORY-16') });
    }
  };
  const unequip = async () => {
    try {
      const r = await app.call('unequip', { body: { slot: item.slot } });
      setInventoryItems((xs) => xs.map((x) => ({ ...x, equipped: r.equipped.some((e) => e.itemId === x.itemId) })));
      if (me) player.value = { ...me, derived: r.stats };
      app.nav.popSheet();
    } catch (e) { app.handleError(e, { resource: tr('CL-SCREENS-INVENTORY-16') }); }
  };
  const toggleLock = async () => {
    try {
      const r = await app.call('lockItem', { body: { itemId: item.itemId, locked: !item.locked } });
      setInventoryItems((xs) => xs.map((x) => (x.itemId === r.itemId ? r : x)));
    } catch (e) { app.handleError(e); }
  };
  const off = !online.value;
  const actions = tutorial
    ? [{ text: tr('UI-BTN-EQUIP'), testId: 'item-equip', onClick: equip }]
    : item.equipped
      ? [{ text: tr('UI-BTN-UNEQUIP'), tone: 'outline' as const, testId: 'item-unequip', disabled: off, onClick: unequip }, { text: tr('UI-BTN-UPGRADE'), testId: 'item-upgrade', disabled: off, onClick: () => app.nav.openSheet('MOD-UPGRADE', { itemId: item.itemId }, { replace: false }) }]
      : [{ text: tr('UI-BTN-UPGRADE'), tone: 'outline' as const, testId: 'item-upgrade', disabled: off, onClick: () => app.nav.openSheet('MOD-UPGRADE', { itemId: item.itemId }) },
        { text: off ? tr('UI-OFFLINE') : low ? tr('CL-SCREENS-INVENTORY-15', { p0: need }) : tr('UI-BTN-EQUIP'), testId: 'item-equip', disabled: off || low, onClick: equip }];
  return (
    <Sheet entry={entry} actions={actions}>
      <div class="col" data-testid="item-card">
        <div class="row">
          <div class={`item-tile rarity-${item.rarity}`} style={{ width: 96, flexShrink: 0 }}><span class="glyph">{slotGlyph(item.slot, 56)}</span></div>
          <div class="col" style={{ gap: 2 }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>{itemName(item.defId)}{item.upgradeLevel ? ` +${item.upgradeLevel}` : ''}</h2>
            <span class={`rarity-${item.rarity}`} style={{ color: 'var(--r)' }}>{RARITY_NAMES[item.rarity]} · {SLOT_NAMES[item.slot]}</span>
            <span class="small muted">{itemDesc(item.defId)}</span>
          </div>
        </div>
        <div class="card col" style={{ gap: 4 }} data-testid="item-compare">
          {compareStats(item, worn).map(({ k, v, diff }) => (
            <div key={k} class="row" style={{ justifyContent: 'space-between' }}>
              <span>{STAT_NAMES[k]}</span>
              <span><b>{fmtStat(k, v)}</b>{worn && !item.equipped && diff !== 0 ? <span class={diff > 0 ? 'pos' : 'neg'}> {diff > 0 ? '+' : '−'}{fmtStat(k, Math.abs(diff))}</span> : null}</span>
            </div>
          ))}
          {worn && !item.equipped ? <span class="small muted">{tr('CL-SCREENS-INVENTORY-17')}{" "}{itemName(worn.defId)}</span> : null}
        </div>
        {!tutorial ? <TransferOffer item={item} /> : null}
        {!tutorial ? (
          <div class="row" style={{ justifyContent: 'space-between' }}>
            <button class="btn small outline" data-testid="item-lock" onClick={toggleLock} disabled={off}><I n={item.locked ? 'lock' : 'unlock'} size={16} /> {item.locked ? tr('CL-SCREENS-INVENTORY-18') : tr('CL-SCREENS-INVENTORY-19')}</button>
            {!item.equipped && !item.locked ? (
              <span class="row" style={{ gap: 16 }}>
                <button class="accent-text" data-testid="item-dismantle" disabled={off} onClick={() => app.nav.openSheet('MOD-DISMANTLE', { itemIds: [item.itemId], mode: 'dismantle' })}>{tr('UI-ITEM-DISMANTLE')}</button>
                <button class="accent-text" disabled={off} onClick={() => app.nav.openSheet('MOD-DISMANTLE', { itemIds: [item.itemId], mode: 'sell' })}>{tr('UI-BTN-SELL')}</button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

/** MOD-UPGRADE: цена и прирост следующего уровня из конфига; нехватка — ST-NOT-ENOUGH, тап → источник валюты. */
export function UpgradeSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['itemId']);
  const item = inventory.value?.items.find((i) => i.itemId === id);
  const w = wallet.value;
  const key = useRef(app.idem());
  if (!item || !w) return null;
  const pv = upgradePreview(item);
  const lackNuts = pv.cost ? Math.max(0, pv.cost.nuts - w.nuts) : 0;
  const lackScrap = pv.cost ? Math.max(0, (pv.cost.scrap ?? 0) - w.scrap) : 0;
  const run = async () => {
    try {
      const r = await app.call('upgradeItem', { body: { itemId: item.itemId }, idempotencyKey: key.current });
      key.current = app.idem();
      setInventoryItems((xs) => xs.map((x) => (x.itemId === r.item.itemId ? r.item : x)));
      setWallet(r.wallet);
      app.haptic('success');
      showToast(tr('CL-SCREENS-INVENTORY-20', { p0: r.item.upgradeLevel }), 'success');
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'MAX_LEVEL') { showToast(tr('CL-SCREENS-INVENTORY-21')); return; }
      app.handleError(e, { resource: tr('UI-RES-NUTS') });
      app.call('getWallet').then(setWallet).catch(() => {});
    }
  };
  const main = pv.atMax
    ? { text: tr('CL-SCREENS-INVENTORY-21'), disabled: true, onClick: () => {} }
    : lackNuts
      ? { text: tr('CL-SCREENS-INVENTORY-22', { p0: amount('nuts', lackNuts) }), tone: 'outline' as const, testId: 'upgrade-lack', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) }
      : lackScrap
        ? { text: tr('CL-SCREENS-INVENTORY-23', { p0: num(lackScrap) }), tone: 'outline' as const, testId: 'upgrade-lack', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'scrap' }) }
        : { text: pv.cost ? tr('CL-SCREENS-INVENTORY-24', { p0: num(pv.cost.nuts), p1: pv.cost.scrap ? tr('CL-SCREENS-INVENTORY-SCRAP-PLUS', { n: pv.cost.scrap }) : '' }) : tr('UI-BTN-UPGRADE'), ...(pv.cost ? { icon: 'nuts' as const } : {}), testId: 'upgrade-go', disabled: !online.value, onClick: run };
  return (
    <Sheet entry={entry} title={tr('UI-UPGRADE-TITLE')} actions={[{ text: tr('UI-BTN-BACK'), tone: 'outline', onClick: () => app.nav.popSheet() }, main]}>
      <div class="col" data-testid="upgrade">
        <div style={{ fontSize: 22, fontWeight: 800, textAlign: 'center' }}>{itemName(item.defId)}: +{item.upgradeLevel} → +{item.upgradeLevel + 1}</div>
        <div class="card col" style={{ gap: 4 }}>
          {STAT_ORDER.filter((k) => item.stats[k] !== undefined).map((k) => (
            <div key={k} class="row" style={{ justifyContent: 'space-between' }}>
              <span>{STAT_NAMES[k]}</span>
              <span>{fmtStat(k, item.stats[k]!)}{pv.next?.[k] !== undefined ? <> → <b class="pos">{fmtStat(k, pv.next[k]!)}</b></> : null}</span>
            </div>
          ))}
        </div>
        {pv.cost ? (
          <div class="row" style={{ justifyContent: 'center', gap: 16 }}>
            <span class={lackNuts ? 'neg' : ''}><I n="nuts" size={16} /> {num(pv.cost.nuts)}</span>
            {pv.cost.scrap ? <span class={lackScrap ? 'neg' : ''}><I n="scrap" size={16} /> {num(pv.cost.scrap)}</span> : null}
          </div>
        ) : <p class="small muted" style={{ margin: 0 }}>{tr('CL-SCREENS-INVENTORY-25')}</p>}
        <p class="small muted" style={{ margin: 0, textAlign: 'center' }}>{tr('CL-SCREENS-INVENTORY-26')}</p>
      </div>
    </Sheet>
  );
}

/** MOD-DISMANTLE ⛔: что уйдёт, что получится, предупреждения; эпические и легендарные — кнопка через 1.5 с. */
export function DismantleDialog({ entry }: SheetProps) {
  const app = useApp();
  const ids = entry.params['itemIds'] as string[];
  const mode = (entry.params['mode'] as 'dismantle' | 'sell') ?? 'dismantle';
  const items = (inventory.value?.items ?? []).filter((i) => ids.includes(i.itemId));
  const rare = items.some((i) => i.rarity === 'epic' || i.rarity === 'legendary');
  const armed = useArmDelay(rare ? 1500 : 0);
  const key = useRef(app.idem());
  const got = dismantleYield(items, clientConfig.value, mode);
  const close = () => { if (app.nav.state.confirm) app.nav.closeConfirm(); else app.nav.popSheet(); };
  const run = async () => {
    try {
      if (mode === 'dismantle') {
        const r = await app.call('dismantle', { body: { itemIds: ids }, idempotencyKey: key.current });
        setInventoryItems((xs) => xs.filter((x) => !r.dismantled.includes(x.itemId)));
        setWallet(r.wallet);
        showToast(tr('CL-SCREENS-INVENTORY-27', { p0: num(r.scrapGained) }), 'success');
      } else {
        const r = await app.call('sellItems', { body: { itemIds: ids }, idempotencyKey: key.current });
        setInventoryItems((xs) => xs.filter((x) => !r.sold.includes(x.itemId)));
        setWallet(r.wallet);
        showToast(tr('CL-SCREENS-INVENTORY-28', { p0: num(r.nutsGained) }), 'success');
      }
      app.haptic('success');
      app.nav.closeSheets();
    } catch (e) {
      app.handleError(e, { resource: tr('CL-SCREENS-INVENTORY-16') });
      close();
    }
  };
  return (
    <Dialog
      title={mode === 'dismantle' ? tr('UI-DISM-TITLE') : tr('CL-SCREENS-INVENTORY-29')}
      testId="MOD-DISMANTLE"
      actions={[
        { text: tr('CL-SCREENS-INVENTORY-01'), tone: 'outline', testId: 'dismantle-cancel', onClick: close },
        { text: armed ? (mode === 'dismantle' ? tr('UI-ITEM-DISMANTLE') : tr('UI-BTN-SELL')) : tr('CL-SCREENS-INVENTORY-30'), tone: 'danger', testId: 'dismantle-go', disabled: !armed, onClick: run },
      ]}
    >
      <div class="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {items.slice(0, 6).map((i) => <span key={i.itemId} class={`badge soft rarity-${i.rarity}`} style={{ border: '2px solid var(--r)' }}>{slotGlyph(i.slot, 14)} {itemName(i.defId)}</span>)}
        {items.length > 6 ? <span class="small muted">{tr('CL-SCREENS-INVENTORY-31')}{" "}{items.length - 6}</span> : null}
      </div>
      <p data-testid="dismantle-get">{tr('CL-SCREENS-INVENTORY-32')}{" "}{got !== null ? (mode === 'dismantle' ? tr('CL-SCREENS-INVENTORY-33', { p0: num(got) }) : tr('CL-SCREENS-INVENTORY-34', { p0: num(got) })) : tr('CL-SCREENS-INVENTORY-35')}</p>
      {rare ? <p class="warn">{tr('CL-SCREENS-INVENTORY-36')}{" "}{items.some((i) => i.rarity === 'legendary') ? tr('CL-SCREENS-INVENTORY-37') : tr('CL-SCREENS-INVENTORY-38')}</p> : null}
      {items.some((i) => i.upgradeLevel > 0) ? <p class="warn">{tr('CL-SCREENS-INVENTORY-39')}</p> : null}
      <p class="small">{mode === 'dismantle' ? tr('CL-SCREENS-INVENTORY-40') : tr('CL-SCREENS-INVENTORY-41')}</p>
    </Dialog>
  );
}
